import React from 'react'
import { Box, styled } from '@mui/material'

import { pxToRem } from '@/shared/styles/themes'
import { NumberedStepsBlock } from '@/slices/blog/mocks/articles'

export interface ArticleNumberedStepsProps {
  block: NumberedStepsBlock
}

export const ArticleNumberedSteps: React.FC<ArticleNumberedStepsProps> = ({ block }) => {
  const { title, steps } = block

  return (
    <StepsWrapper>
      {title && <StepsTitle>{title}</StepsTitle>}
      <StepsList>
        {steps.map((step, i) => (
          <StepItem key={i}>
            <StepNumberColumn>
              <StepCircle>{i + 1}</StepCircle>
              {i < steps.length - 1 && <StepConnector />}
            </StepNumberColumn>
            <StepContent>
              <StepLabel>{step.title}</StepLabel>
              <StepDescription>{step.description}</StepDescription>
            </StepContent>
          </StepItem>
        ))}
      </StepsList>
    </StepsWrapper>
  )
}

const StepsWrapper = styled(Box)(({ theme }) => ({
  margin: `${pxToRem(36)} 0`,

  [theme.breakpoints.up('md')]: {
    margin: `${pxToRem(44)} 0`,
  },
}))

const StepsTitle = styled('h3')(({ theme }) => ({
  fontSize: pxToRem(20),
  fontWeight: 700,
  color: theme.palette.primary.main,
  marginBottom: pxToRem(24),
  margin: 0,
  marginBlockEnd: pxToRem(24),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(22),
    marginBlockEnd: pxToRem(28),
  },
}))

const StepsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

const StepItem = styled(Box)({
  display: 'flex',
  gap: pxToRem(16),

  '&:last-child': {
    '& .step-connector': {
      display: 'none',
    },
  },
})

const StepNumberColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flexShrink: 0,
})

const StepCircle = styled(Box)(({ theme }) => ({
  width: pxToRem(36),
  height: pxToRem(36),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: pxToRem(14),
  fontWeight: 700,
  flexShrink: 0,

  [theme.breakpoints.up('md')]: {
    width: pxToRem(40),
    height: pxToRem(40),
    fontSize: pxToRem(15),
  },
}))

const StepConnector = styled(Box)(({ theme }) => ({
  width: pxToRem(2),
  flex: 1,
  minHeight: pxToRem(16),
  backgroundColor: theme.palette.divider,
  className: 'step-connector',
}))

StepConnector.defaultProps = {
  className: 'step-connector',
}

const StepContent = styled(Box)(({ theme }) => ({
  paddingBottom: pxToRem(28),
  paddingTop: pxToRem(6),

  [theme.breakpoints.up('md')]: {
    paddingBottom: pxToRem(32),
  },
}))

const StepLabel = styled('h4')(({ theme }) => ({
  fontSize: pxToRem(17),
  fontWeight: 700,
  color: theme.palette.primary.main,
  margin: 0,
  marginBlockEnd: pxToRem(8),
  lineHeight: 1.3,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
  },
}))

const StepDescription = styled('p')(({ theme }) => ({
  fontSize: pxToRem(15),
  fontWeight: 300,
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  margin: 0,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
  },
}))
